// Sentry bootstrap + helpers. main.tsx sadece initSentry() çağırır.
// Tag şeması + noise filter + scrubber plan'daki "Sentry Prod-Ready Aktivasyonu →
// Ortak Sözleşmeler" ile birebir. 3 panel (tenant-panel, customer-portal, admin-panel)
// arasında DRY yok; değişiklik yapılırken üçü de senkron tutulmalı.
// Farklılık yalnızca deployable tag'inde — PROJECT_TAG ve DSN env var'ı hepsinde aynı.
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

const PROJECT_TAG = 'mentis';

const IGNORE_ERRORS: (string | RegExp)[] = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
  'Non-Error exception captured',
  'NetworkError when attempting to fetch resource',
  'Failed to fetch',
  'Load failed',
  'ChunkLoadError',
  /Loading chunk \d+ failed/,
];

const DENY_URLS: RegExp[] = [
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-extension:\/\//,
];

const SENSITIVE_KEY_PATTERN =
  /(password|passwordhash|token|refreshtoken|secret|apikey|api[_-]?key|cardnumber|card[_-]?no|cvv|cvc|iban|licensekey|tckno|tckimlikno)/i;

const SENSITIVE_HEADERS = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
]);

function maskSensitiveRecord(record: Record<string, unknown> | undefined) {
  if (!record) return;
  for (const key of Object.keys(record)) {
    if (SENSITIVE_HEADERS.has(key.toLowerCase()) || SENSITIVE_KEY_PATTERN.test(key)) {
      record[key] = '[Filtered]';
    }
  }
}

function maskSensitiveString(value: string): string {
  return value.replace(
    /([?&](?:token|key|password|secret|apikey|api_key)=)[^&]+/gi,
    '$1[Filtered]',
  );
}

function scrubPii(event: ErrorEvent): ErrorEvent | null {
  try {
    if (event.request) {
      maskSensitiveRecord(event.request.headers as Record<string, unknown> | undefined);
      if (typeof event.request.query_string === 'string') {
        event.request.query_string = maskSensitiveString(event.request.query_string);
      }
      if (typeof event.request.url === 'string') {
        event.request.url = maskSensitiveString(event.request.url);
      }
      if (event.request.data && typeof event.request.data === 'object') {
        maskSensitiveRecord(event.request.data as Record<string, unknown>);
      }
    }

    // User context — email/name asla gönderilmemeli; setSentryUser() bunu garantiliyor
    // ama defansif olarak burada da temizle.
    if (event.user) {
      delete event.user.email;
      delete (event.user as { username?: string }).username;
      delete (event.user as { name?: string }).name;
    }

    if (Array.isArray(event.breadcrumbs)) {
      for (const crumb of event.breadcrumbs) {
        if (typeof crumb.message === 'string') {
          crumb.message = maskSensitiveString(crumb.message);
        }
        if (crumb.data && typeof crumb.data === 'object') {
          maskSensitiveRecord(crumb.data as Record<string, unknown>);
        }
      }
    }
  } catch {
    // scrub hatası event'i düşürmesin
  }
  return event;
}

export function initSentry(deployable: string) {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
    import.meta.env.MODE;

  // Release: tek kaynak vite-plugin (SENTRY_RELEASE env build-time embed). VITE_APP_VERSION
  // kullanımı kaldırıldı — duplicate release kaynağı karışıklık yaratıyordu.
  const release = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;

  const tracesSampleRate = Number(
    (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined) ??
      (import.meta.env.PROD ? '0.1' : '1.0'),
  );

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate,
    tracePropagationTargets: [/^\/api/, /destekgor\.com\/api/],
    // replaysSessionSampleRate 0.2'den 0'a düşürüldü — maliyet kontrolü (plan).
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    attachStacktrace: true,
    ignoreErrors: IGNORE_ERRORS,
    denyUrls: DENY_URLS,
    beforeSend: scrubPii,
  });

  // Router fazında webhook bu tag'lere bakacak — naming geriye dönük değiştirilmemeli.
  Sentry.setTags({
    project: PROJECT_TAG,
    deployable,
    'service.name': `${PROJECT_TAG}-${deployable}`,
  });
}

export interface SentryUserContext {
  id: string;
  role?: string;
  tenantSlug?: string;
}

export function setSentryUser(user: SentryUserContext | null) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  // email/name asla set edilmez (plan PII kuralı).
  Sentry.setUser({ id: user.id });
  Sentry.setTag('user.id', user.id);
  if (user.role) Sentry.setTag('role', user.role);
  if (user.tenantSlug) Sentry.setTag('tenant', user.tenantSlug);
}

export function clearSentryUser() {
  Sentry.setUser(null);
}

export function setSentryCorrelationId(correlationId: string) {
  Sentry.setTag('correlation_id', correlationId);
}
